import {
  type DNDData,
  type DropTargetDropEvent,
  type DropTargetOptions,
  useDropTarget,
} from '@affine/component';
import { NavigationPanelService } from '@affine/core/modules/navigation-panel';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useRef } from 'react';

import {
  SIDEBAR_AREA_DRAG_HOVER_EXPAND_DELAY_MS,
  SIDEBAR_AREA_HEADER_DROP_TARGET,
} from '../../desktop/components/navigation-panel/tree/sidebar-area-drag-hover';

export function useSidebarAreaHeaderDropTarget<
  D extends DNDData = AffineDNDData,
>({
  canDrop,
  collapsed,
  onExpand,
  onDrop,
  path,
}: {
  canDrop: DropTargetOptions<D>['canDrop'];
  collapsed: boolean;
  onExpand?: () => void;
  onDrop: (data: DropTargetDropEvent<D>) => void;
  path: string[];
}) {
  const navigationPanelService = useService(NavigationPanelService);
  const expandTimerRef = useRef<number | null>(null);
  const cancelPendingExpand = useCallback(() => {
    if (expandTimerRef.current !== null) {
      window.clearTimeout(expandTimerRef.current);
      expandTimerRef.current = null;
    }
  }, []);
  const scheduleExpand = useCallback(() => {
    if (!collapsed || expandTimerRef.current !== null) return;
    expandTimerRef.current = window.setTimeout(() => {
      expandTimerRef.current = null;
      if (onExpand) {
        onExpand();
      } else {
        navigationPanelService.setCollapsed(path, false);
      }
    }, SIDEBAR_AREA_DRAG_HOVER_EXPAND_DELAY_MS);
  }, [collapsed, navigationPanelService, onExpand, path]);
  const handleDrop = useCallback(
    (data: DropTargetDropEvent<D>) => {
      cancelPendingExpand();
      onDrop(data);
    },
    [cancelPendingExpand, onDrop]
  );

  useEffect(() => cancelPendingExpand, [cancelPendingExpand]);
  useEffect(() => {
    if (!collapsed) cancelPendingExpand();
  }, [cancelPendingExpand, collapsed]);

  return useDropTarget<D>(
    () => ({
      allowExternal: true,
      canDrop,
      data: {
        at: collapsed
          ? SIDEBAR_AREA_HEADER_DROP_TARGET
          : 'navigation-panel:organize:root',
      },
      dropEffect: 'move',
      onDragEnter: scheduleExpand,
      onDragLeave: cancelPendingExpand,
      onDrop: handleDrop,
    }),
    [canDrop, cancelPendingExpand, collapsed, handleDrop, scheduleExpand]
  );
}
